/**
 * Product reviews web component (PDP).
 *
 * The first 50 reviews are server-rendered into the section from the
 * `custom.reviews_first_page` product metafield (cheap; no API call needed
 * for first paint). Pagination beyond that fetches via the public review
 * listing API.
 *
 * This component handles:
 *  - Rendering server-side initial reviews into the list (parsed from a
 *    JSON script tag the section emits).
 *  - "Load more" pagination via reviews-client.
 *  - "Write a review" link to /pages/write-review?p={id}.
 *  - Sort dropdown (newest / highest / lowest).
 */

import { ReviewsClient } from './reviews-client-v2.js';

class ProductReviews extends HTMLElement {
  constructor() {
    super();
    this.client = new ReviewsClient();
    this.page = 1;
    this.sort = 'newest';
    this.totalPages = 1;
  }

  async connectedCallback() {
    if (this.dataset.mounted === '1') return;
    this.dataset.mounted = '1';

    this.productId = this.dataset.productId;
    this.firstPagePreloaded = this.dataset.firstPagePreloaded === '1';

    this.refs = {
      list: this.querySelector('[data-list]'),
      empty: this.querySelector('[data-empty]'),
      sort: this.querySelector('[data-sort]'),
      controls: this.querySelector('[data-controls]'),
      loadMore: this.querySelector('[data-load-more]'),
      pagination: this.querySelector('[data-pagination]'),
      avg: this.querySelector('[data-avg]'),
      count: this.querySelector('[data-count]'),
      stars: this.querySelector('[data-stars]'),
    };

    this.refs.sort?.addEventListener('change', (e) => this.onSortChange(e));
    this.refs.loadMore?.addEventListener('click', () => this.loadMore());

    // The first-page-preloaded scenario: server already rendered reviews
    // from the metafield. We don't refetch — just hook up pagination.
    if (this.firstPagePreloaded) {
      const totalCount = parseInt(this.dataset.totalCount ?? '0', 10);
      const pageSize = parseInt(this.dataset.pageSize ?? '10', 10);
      this.totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      this.updatePagination();
    } else {
      // Fallback: fetch first page client-side (e.g. when metafield is empty).
      await this.fetchAndAppend({ replace: true });
    }
  }

  async onSortChange(e) {
    this.sort = e.target.value;
    this.page = 1;
    await this.fetchAndAppend({ replace: true });
  }

  async loadMore() {
    if (this.page >= this.totalPages) return;
    this.page += 1;
    await this.fetchAndAppend({ replace: false });
  }

  async fetchAndAppend({ replace }) {
    if (!this.productId || !this.refs.list) return;
    if (this.refs.loadMore) this.refs.loadMore.disabled = true;
    try {
      const resp = await this.client.listForProduct(this.productId, {
        page: this.page,
        pageSize: 10,
        sort: this.sort,
      });
      this.totalPages = resp.pagination?.totalPages ?? 1;
      const publishedCount = resp.aggregate?.publishedCount ?? 0;
      this.renderAggregate(resp.aggregate);
      if (replace) this.refs.list.innerHTML = '';
      if ((resp.reviews ?? []).length === 0 && this.page === 1) {
        if (this.refs.empty) this.refs.empty.hidden = false;
        if (this.refs.list) this.refs.list.hidden = true;
      } else {
        if (this.refs.empty) this.refs.empty.hidden = true;
        if (this.refs.list) this.refs.list.hidden = false;
        for (const r of resp.reviews ?? []) {
          this.refs.list.appendChild(this.renderReview(r));
        }
      }
      // Sort controls: visible whenever there's at least one review.
      if (this.refs.controls) this.refs.controls.hidden = publishedCount === 0;
      this.updatePagination();
    } catch (err) {
      console.error('[product-reviews] fetch failed', err);
    } finally {
      if (this.refs.loadMore) this.refs.loadMore.disabled = false;
    }
  }

  renderAggregate(agg) {
    if (!agg) return;
    if (this.refs.avg) {
      this.refs.avg.textContent = agg.avgRating
        ? Number(agg.avgRating).toFixed(1)
        : '—';
    }
    if (this.refs.count) {
      const n = agg.publishedCount ?? 0;
      this.refs.count.textContent =
        n === 1 ? '1 review' : `${n.toLocaleString()} reviews`;
    }
    if (this.refs.stars) {
      this.refs.stars.innerHTML = renderStars(agg.avgRating);
    }
  }

  renderReview(r) {
    const article = document.createElement('article');
    article.className = 'product-reviews__item';
    const date = new Date(r.created_at).toLocaleDateString();
    const verified = r.verified
      ? '<span class="product-reviews__verified" aria-label="Verified buyer">Verified buyer</span>'
      : '';
    const title = r.title
      ? `<h3 class="product-reviews__item-title">${escape(r.title)}</h3>`
      : '';
    article.innerHTML = `
      <header class="product-reviews__item-head">
        <span class="product-reviews__item-stars" aria-label="${r.rating} out of 5">${renderStars(r.rating)}</span>
        ${title}
      </header>
      <p class="product-reviews__item-body">${escape(r.body)}</p>
      <footer class="product-reviews__item-foot">
        <span class="product-reviews__item-name">${escape(r.name)}</span>
        ${verified}
        <time class="product-reviews__item-date" datetime="${r.created_at}">${date}</time>
      </footer>
    `;
    return article;
  }

  updatePagination() {
    if (!this.refs.pagination) return;
    if (this.totalPages <= 1 || this.page >= this.totalPages) {
      this.refs.pagination.hidden = true;
    } else {
      this.refs.pagination.hidden = false;
    }
  }
}

function renderStars(rating) {
  const full = Math.round(Number(rating) || 0);
  let out = '';
  for (let i = 1; i <= 5; i++) {
    out += i <= full ? '★' : '☆';
  }
  return out;
}

function escape(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}
